import React from 'react';
import { SwiperSlide } from 'swiper/react';

import {
    Carousel,
    CarouselWrapper,
    CarouselArrowNavigation,
    CarouselPagination
} from '$partials/base/carousel';
import { PropertyAreaText, PropertyPriceText } from '$partials/properties';
import { SimpleImage } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').FeaturedPropertyCarouselCard}
 */
const PropertyCard = (props) => {
    if (props.isOffMarket) { return null; }

    return (
        <>
            {/* Desktop */}
            <div className='h-full hidden md:flex flex-row group'>
                <div className='w-7/11 h-full col-padding'>
                    <div className='w-full h-full overflow-hidden relative'>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1,
                                grabCursor: false,
                                nested: true,
                                allowTouchMove: false,
                                spaceBetween: 0,
                            }}>
                            <CarouselWrapper>
                                {props?.images?.map((item) => {
                                    return (
                                        <SwiperSlide
                                            aria-roledescription='slide'
                                            className='select-none cursor-pointer w-full h-full relative'
                                            key={item.id}
                                            role='group'>
                                            <SimpleImage
                                                className='w-full h-full object-cover aspect-[3/2]'
                                                {...item.media?.image}/>
                                            <div className='absolute inset-0 opacity-0 group-hover:opacity-100 before:absolute before:inset-0 before:bg-[rgba(0,0,0,0.4)] before:mix-blend-saturation before:content-[""] after:absolute after:inset-0 after:bg-[rgba(30,37,165,0.5)] after:mix-blend-screen after:content-[""]' />
                                        </SwiperSlide>
                                    );
                                })}
                                {props?.images?.length > 1 && (
                                    <CarouselPagination totalSlides={props.images.length} />
                                )}
                            </CarouselWrapper>
                        </Carousel>
                    </div>
                </div>

                <div className={`col-padding-r w-4/11 flex flex-col justify-between ${props.index < props.propertyItems.length - 1 ? 'border-r border-main-border' : ''}`}>
                    {props.displayAddress && (
                        <div>
                            {props.price &&
                                <div className='flex flex-col h-20 justify-center w-fit mb-16'>
                                    <div className='bg-tag-main-background border border-tag-main-border rounded flex h-24 items-center justify-center px-8 py-8'>
                                        <p className='type-style-eyebrow text-main-brand-text'>
                                            <PropertyPriceText
                                                isOffMarket={props.isOffMarket}
                                                pageType={props.__typename}
                                                price={props.price}
                                                priceCurrency={props.priceCurrency}
                                                priceRentPeriod={props.priceRentPeriod} />
                                        </p>
                                    </div>
                                </div>
                            }

                            <p className='type-style-eyebrow text-main-text-secondary group-hover:text-main-brand-text'>
                                {props.displayAddress}
                            </p>

                            {/* Bed/Bath/livingAreaMeters Details */}
                            <div className='flex gap-16 items-center type-style-eyebrow text-main-text-primary mt-4 group-hover:text-main-brand-text'>
                                {props.bedrooms && (
                                    <p className='shrink-0'>
                                        {props.bedrooms} bed
                                    </p>
                                )}
                                {props.bathrooms && (
                                    <p className='shrink-0'>
                                        {props.bathrooms} bath
                                    </p>
                                )}
                                {props.livingAreaMeters && (
                                    <p className='shrink-0'>
                                        <PropertyAreaText
                                            areaMeters={props.livingAreaMeters}
                                            isOffMarket={props.isOffMarket} />
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {props?.title && (
                        <h3 className='type-style-2 text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text'>
                            <a href={props?.url}>
                                {props?.title}
                            </a>
                        </h3>
                    )}
                </div>
            </div>

            {/* Mobile/Tablet */}
            <div
                className={`col-padding md:hidden flex flex-col gap-16 w-full group ${props.index < props.propertyItems.length - 1 ? 'border-r border-main-border' : ''}`}>
                <div className='w-full h-full'>
                    <div className='aspect-[3/2] w-full h-full overflow-hidden relative'>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1,
                                grabCursor: false,
                                nested: true,
                                spaceBetween: 0,
                            }}>
                            <CarouselWrapper>
                                {props?.images?.map((item) => {
                                    return (
                                        <SwiperSlide
                                            aria-roledescription='slide'
                                            className='select-none cursor-pointer w-full h-full relative'
                                            key={item.id}
                                            role='group'>
                                            <SimpleImage
                                                className='w-full h-full object-cover'
                                                {...item.media?.image}/>
                                        </SwiperSlide>
                                    );
                                })}
                                {props?.images?.length > 1 && (
                                    <CarouselPagination totalSlides={props.images.length} />
                                )}
                            </CarouselWrapper>
                        </Carousel>
                    </div>
                </div>

                {/* Property Details */}
                <div className='flex flex-col gap-16 w-full'>
                    {/* displayAddress and Price */}
                    <div className='flex flex-col gap-y-4 w-full'>
                        <div className='flex gap-8 items-center w-full'>
                            <p className='hidden sm:block type-style-eyebrow flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-main-text-primary'>
                                {props.displayAddress}
                            </p>

                            {props.price &&
                                <div className='flex flex-col h-20 justify-center shrink-0'>
                                    <div className='bg-tag-main-background border border-tag-main-border rounded flex h-24 items-center justify-center px-8 py-8'>
                                        <p className='type-style-eyebrow text-main-brand-text'>
                                            <PropertyPriceText
                                                isOffMarket={props.isOffMarket}
                                                pageType={props.__typename}
                                                price={props.price}
                                                priceCurrency={props.priceCurrency}
                                                priceRentPeriod={props.priceRentPeriod} />
                                        </p>
                                    </div>
                                </div>
                            }
                        </div>

                        <p className='block sm:hidden mt-16 type-style-eyebrow flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-main-text-primary'>
                            {props.displayAddress}
                        </p>

                        {/* Bed/Bath/livingAreaMeters Details */}
                        <div className='flex gap-16 items-center type-style-eyebrow text-main-text-primary'>
                            {props.bedrooms && (
                                <p className='shrink-0'>
                                    {props.bedrooms} bed
                                </p>
                            )}
                            {props.bathrooms && (
                                <p className='shrink-0'>
                                    {props.bathrooms} bath
                                </p>
                            )}
                            {props.livingAreaMeters && (
                                <p className='shrink-0'>
                                    <PropertyAreaText
                                        areaMeters={props.livingAreaMeters}
                                        isOffMarket={props.isOffMarket} />
                                </p>
                            )}
                        </div>
                    </div>

                    {/* title and Tags */}
                    <div className='flex flex-col gap-24 w-full pr-0 md:pr-40'>
                        {props.title && (
                            <h3 className='type-style-2 text-main-text-primary whitespace-pre-wrap'>
                                <a href={props.url}>
                                    {props.title}
                                </a>
                            </h3>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').FeaturedPropertyCarousel}
 */
const FeaturedPropertyCarousel = ({
    heading,
    propertyItems
}) => {

    const swiperConfig = {
        slidesPerView: 'auto',
        spaceBetween: 0
    };

    return (
        <div className='module-featured-property-carousel theme-light-alt bg-main-background py-16 sm:py-40 md:py-48 '>
            <div className='overflow-hidden w-full max-w-screen left-1/2 relative -translate-x-1/2'>
                <div className='layout-container layout-site-outer-padding overflow-visible'>
                    <div className='col-wrapper'>
                        {propertyItems?.length > 1 ? (
                            <Carousel swiperConfig={swiperConfig}>
                                {/* Header with title and controls */}
                                <div className='col-padding w-full flex justify-between mb-24'>
                                    {/* Title */}
                                    {heading && (
                                        <h2 className='type-style-2 text-main-text-primary w-full'>
                                            {heading}
                                        </h2>
                                    )}

                                    {/* Controls */}
                                    <div className='hidden md:flex items-center justify-end gap-16'>
                                        <CarouselArrowNavigation />
                                    </div>
                                </div>
                                <CarouselWrapper>
                                    {propertyItems?.map((property, index) => (
                                        <SwiperSlide
                                            className='w-7/12 sm:w-8/12 md:w-11/12'
                                            key={property?.property.id || index}>
                                            <PropertyCard
                                                className='col-padding'
                                                index={index}
                                                propertyItems={propertyItems}
                                                {...property?.property} />

                                        </SwiperSlide>
                                    ))}
                                </CarouselWrapper>
                            </Carousel>
                        ) : (
                            <>
                                {/* Header */}
                                {heading && (
                                    <div className='col-padding w-full mb-24'>
                                        <h2 className='type-style-2 text-main-text-primary'>
                                            {heading}
                                        </h2>
                                    </div>
                                )}
                                {/* Single Property */}
                                {propertyItems?.[0] && (
                                    <PropertyCard
                                        index={0}
                                        propertyItems={propertyItems}
                                        {...propertyItems[0]?.property} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default FeaturedPropertyCarousel;
